import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useStore } from "../context/StoreContext";

export default function SearchOverlay() {
    const { searchOpen, setSearchOpen, products } = useStore();
    const [q, setQ] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (searchOpen) {
            setQ("");
            setTimeout(() => inputRef.current?.focus(), 350);
        }
    }, [searchOpen]);

    const results = q.trim()
        ? products.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)
        : [];

    return (
        <AnimatePresence>
            {searchOpen && (
                <motion.div
                    data-testid="search-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="fixed inset-0 z-[180] bg-[rgba(242,240,234,0.97)] backdrop-blur-xl"
                >
                    <button
                        data-testid="search-close-button"
                        onClick={() => setSearchOpen(false)}
                        aria-label="Close search"
                        className="absolute right-6 top-6 text-[#8E8D8A] transition-colors hover:text-[#0A0A0A]"
                    >
                        <X size={22} strokeWidth={1.5} />
                    </button>
                    <div className="mx-auto max-w-3xl px-6 pt-32">
                        <motion.div
                            initial={{ y: 40, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <p className="mb-6 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">Search</p>
                            <input
                                ref={inputRef}
                                data-testid="search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="TYPE TO SEARCH"
                                className="w-full border-b border-[rgba(10,10,10,0.2)] bg-transparent pb-4 font-display text-3xl font-extrabold uppercase tracking-tight text-[#0A0A0A] outline-none placeholder:text-[#333] focus:border-[#0A0A0A] sm:text-5xl"
                            />
                        </motion.div>
                        <div className="mt-10 space-y-1">
                            <AnimatePresence>
                                {results.map((p, i) => (
                                    <motion.div
                                        key={p.id}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ delay: i * 0.05, duration: 0.4 }}
                                    >
                                        <Link
                                            data-testid={`search-result-${p.id}`}
                                            to={`/product/${p.id}`}
                                            onClick={() => setSearchOpen(false)}
                                            className="group flex items-center gap-5 py-3"
                                        >
                                            <img src={p.front} alt={p.name} className="h-16 w-[52px] object-cover" />
                                            <span className="link-underline font-display text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
                                                {p.name}
                                            </span>
                                            <span className="ml-auto text-xs text-[#8E8D8A]">${p.price} CAD</span>
                                        </Link>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            {q.trim() && results.length === 0 && (
                                <p data-testid="search-no-results" className="pt-6 text-xs uppercase tracking-[0.25em] text-[#6B6A66]">
                                    No results for "{q}"
                                </p>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
